<template>
  <div class="text-bold text-medium-blue" style="margin-bottom: 16px;">What’s included:</div>
  <div v-if="isStayBundle" class="buybox__bundle buybox__bundle-generic bundle-block" 
      :class="{ 'bundle-block-active': product.bundleFirst }" 
      :data-bundle-product="product.bundleProduct"
      :data-bundle-index="product.bundleIndex"
      :data-otp-bundle-min="product.otpBundleMin"
      :data-otp-bundle-max="product.otpBundleMax"
      :data-sub-bundle-min="product.subBundleMin"
      :data-sub-bundle-max="product.subBundleMax"
      :data-product-base-price="product.productBasePrice">

    <div v-if="product.subBundleProductsCount > 0" 
        :data-bundle-min="product.subBundleMin" 
        :data-bundle-max="product.subBundleMax" 
        class="buybox__bundle-generic-product-set-list bundle-product-set-list" 
        data-bundle-product-sub-list>

      <template v-for="group in groupedVariants" :key="group.selectIndex">
        <div v-if="group.variants.length > 1"
             class="buybox__bundle-generic-product-wrapper option-chip buybox__bundle-product-group"
             :data-select-index="group.selectIndex">

          <div class="buybox__bundle-generic-product-flavor text-primary-active">
            <svg class="icon"><use href="#check"></use></svg>
            <a :href="group.variants[0].product_url">{{ group.variants[0].product_title }}</a>
          </div>

          <div class="text-sm text-dark-gray-9">Choose Flavor:</div>

          <div ref="dropdownElement" 
            :class="['dropdown' , {'open': isVariantDrpdwnOpen}]" 
            @click="toggleVariantDrpdwn">
            <div class="dropdown-selected" 
              :class="['flavor', (allVariantsWithGlobalIndex.find(v => v.id === selectedVariants[group.selectIndex])?.flavor ?? group.variants[0].flavor).toLowerCase().replace(/ /g, '-')].join('-')">
                {{ allVariantsWithGlobalIndex.find(v => v.id === selectedVariants[group.selectIndex])?.title ?? group.variants[0].title }}
            </div>
            <svg class="dropdown-arrow"><use href="#caret-down"></use></svg>

            <div class="dropdown-options buybox__bundle-variants-radio">
              <template v-for="variant in group.variants" :key="variant.id">
                <label 
                    v-if="variant.inventory_quantity > 0"
                    class="buybox__bundle-variant-radio-label dropdown-option"
                    :class="{ 'selected': selectedVariantId(group.selectIndex) === variant.id }"
                    @click.stop="selectVariant(variant)">

                  <input type="radio" class="hide"
                      :name="'bundle-group-' + group.selectIndex"
                      :value="variant.id"
                      :checked="selectedVariantId(group.selectIndex) === variant.id"
                      @change="selectVariant(group.selectIndex, variant)">

                  <div class="variant-title" :class="['flavor', variant.flavor.toLowerCase().replace(/ /g, '-')].join('-')">{{ variant.title }}</div>

                  <span class="variant-debug text-xs hide">
                    ID: {{ variant.id }} | Index: {{ variant.globalIndex }}
                  </span>
                </label>
              </template>
            </div>
          </div>

          <div class="buybox__bundle-product-select-quantity-wrap hide"
              :data-buybox-id="group.variants[0].globalIndex"
              :data-price="allVariantsWithGlobalIndex.find(v => v.id === selectedVariants[group.selectIndex])?.price ?? group.variants[0].price"
              :data-product="selectedVariants[group.selectIndex] ?? group.variants[0].id">

            <input
                type="hidden"
                class="bundle-product-select-quantity-input"
                value="1"
                :data-price="allVariantsWithGlobalIndex.find(v => v.id === selectedVariants[group.selectIndex])?.price ?? group.variants[0].price"
                :data-product="selectedVariants[group.selectIndex] ?? group.variants[0].id"
              >
          </div>
          
        </div>

        <div v-else-if="group.variants[0].inventory_quantity > 0"
            class="buybox__bundle-generic-product-wrapper option-chip single-variant"
            :data-bundle-variant-select-id="group.variants[0].id"
            :data-global-index="group.variants[0].globalIndex">

          <div class="buybox__bundle-generic-product-flavor text-base text-primary-active">
            <svg class="icon"><use href="#check"></use></svg>
            <a :href="group.variants[0].product_url">{{ group.variants[0].product_title }}</a>
          </div>
          <div class="text-xs hide">Variant: {{ group.variants[0].title }}</div>
          <div class="text-xs hide">Variant id: {{ group.variants[0].id }}</div>
          <div class="text-xs hide">Item index: {{ group.variants[0].globalIndex }}</div>

          <div class="buybox__bundle-product-select-quantity-wrap hide"
               :data-buybox-id="group.variants[0].globalIndex"
               :data-price="group.variants[0].price"
               :data-product="group.variants[0].id">
            <input type="hidden"
                class="bundle-product-select-quantity-input"
                :value="1"
                :data-price="group.variants[0].price"
                :data-product="group.variants[0].id">
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script setup>
  import { toRefs, ref, computed, onMounted } from 'vue';

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },

    selectedVariant: {
      type: Object,
      default: null,
    },

    isSubscription: {
      type: Boolean,
      default: false, 
    },

    isStayBundle: {
      type: Boolean,
      default: false, 
    }
  });

  const { product, isSubscription, isStayBundle } = toRefs(props);

  const emit = defineEmits(['update-card-price'])


  const allVariantsWithGlobalIndex = computed(() => {
    let globalIndex = 0
    return props.product.bundleProductSelect.flatMap(select =>
      select.variants.map(variant => ({
        ...variant,
        globalIndex: globalIndex++,
        selectIndex: select.product_id
      }))
    )
  })

  const groupedVariants = computed(() => {
    const groups = {};
    allVariantsWithGlobalIndex.value.forEach(v => {
      if (!groups[v.selectIndex]) {
        groups[v.selectIndex] = { selectIndex: v.selectIndex, variants: [] };
      }
      groups[v.selectIndex].variants.push(v);
    });
    return Object.values(groups);
  });


  const selectedVariants = ref({});

  const selectedVariantId = (selectIndex) => selectedVariants.value[selectIndex];

  const cartQuantities = ref({});

  onMounted(async () => {
    groupedVariants.value.forEach(group => {
      const firstVariant = group.variants[0];
      selectVariant(group.selectIndex, firstVariant);
    });
    
    const bundleContainer = document.querySelector(`[id="${product.value.bundleProduct}"]`);

    if (!bundleContainer) return;

    const radio = document.querySelector(`[id="${product.value.bundleProduct}"] .buybox__bundle-variants-radio`);
    const singleVariant = document.querySelector(`[id="${product.value.bundleProduct}"] .single-variant`);

    const target = radio || singleVariant;

    if (target) {
        atcEnableValidation({ target });
    }
  });

  const selectVariant = async (selectIndex, variant) => {
    const oldVariantId = selectedVariants.value[selectIndex];

    selectedVariants.value[selectIndex] = variant?.id ?? null;

    if (oldVariantId) {
      const oldVariant = allVariantsWithGlobalIndex.value.find(v => v.id === oldVariantId);
      if (oldVariant) cartQuantities.value[oldVariant.globalIndex] = 0;
    }

    if (variant?.globalIndex !== undefined) {
      cartQuantities.value[variant.globalIndex] = 1;
    }

    const radio = document.querySelector(`[id="${product.value.bundleProduct}"] .buybox__bundle-variants-radio`);

    if (radio) {
        atcEnableValidation({ target: radio });
        isVariantDrpdwnOpen.value = false;
    }
  };

  const getTotalQuantity = () => {
    return Object.values(cartQuantities.value).reduce((sum, qty) => sum + (qty || 0), 0);
  };

  const atcGetBundleInterval = async (event) => {
    if(isSubscription.value) {
      return 'sub';
    } else if(!isSubscription.value) {
      return 'otp';
    }

    return 'err';
  }

  const atcGetContentBlock = async (event) => {
    const contentBlock = event.target.closest(`[id="${product.value.bundleProduct}"].buybox__content`);
    return contentBlock;
  }

  const atcGetBundleBlock = async (event) => {
    const contentBlock = event.target.closest(`[id="${product.value.bundleProduct}"].buybox__content`);
    const bundleBlock = contentBlock ? contentBlock.querySelector('.buybox__bundle') : null;
    return bundleBlock;
  }

  const atcGetBundleQuantity = async (event) => {
    const totalQuantity = getTotalQuantity();
    return totalQuantity;
  };

  const atcEnableValidation = async (event) => {
    const contentBlock = event.target.closest(`[id="${product.value.bundleProduct}"].buybox__content`);
    const bundleBlock = await atcGetBundleBlock(event);
    const addToCartButtons = contentBlock ? document.querySelectorAll('.buybox__atc .bundle-submit-button') : null;
    const bundleQuantity = await atcGetBundleQuantity(event);
    const interval = await atcGetBundleInterval(event);
    
    const bundleMin = (interval == 'otp' ? product.value.otpBundleMin : product.value.subBundleMin);
    const bundleMax = (interval == 'otp' ? product.value.otpBundleMax : product.value.subBundleMax);
    
    const quantityToAdd = bundleMin - bundleQuantity;

    addToCartButtons.forEach((addToCartButton) => {
      const addToCartText = addToCartButton.querySelector('.bundle-submit-button-atc-text');
      const addMoreText = addToCartButton.querySelector('[data-bundle-submit-button-add-more-text]');
      const addMoreQuantity = addToCartButton.querySelector('[data-bundle-submit-button-add-more-quantity]');

      if (bundleQuantity >= bundleMin && (bundleQuantity <= bundleMax || bundleMax < bundleMin)) {
        addToCartButton.removeAttribute('disabled');
        addToCartText.classList.remove('hide');
        addMoreText.classList.add('hide');
        bundleBlock.classList.add('bundle-checkout-enabled');
      } else {
        addToCartButton.setAttribute('disabled', '');
        addToCartText.classList.add('hide');
        addMoreText.classList.remove('hide');
        addMoreQuantity.innerHTML = quantityToAdd;
        bundleBlock.classList.remove('bundle-checkout-enabled');
      }
    });

    await updateBundlePrice(event);
  }

  const updateBundlePrice = async (event) => {
    const contentBlock = await atcGetContentBlock(event);
    const bundleBlock = await atcGetBundleBlock(event);
    const otpPriceEls = contentBlock ? document.querySelectorAll('.otp__input-price') : null;
    const otpPriceDiscountEl = bundleBlock ? document.querySelector('.otp__input') : null;
    const otpDiscountPriceEls = contentBlock ? document.querySelectorAll('.otp__input-price-discounted') : null;
    const subPriceEls = contentBlock ? document.querySelectorAll('.subscription__input-price') : null;
    const curPriceEls = contentBlock ? document.querySelectorAll('.bundle-submit-button-atc-text') : null;
    const frequency = contentBlock.querySelector('.subscription__input');
    const productList = contentBlock.querySelector('.bundle-product-set-list');
    const productBasePrice = parseInt(bundleBlock.dataset.productBasePrice);

    const interval = await atcGetBundleInterval(event);
    let otpSubtotal = 0;
    let otpSubtotalDiscounted = 0;
    let subSubtotal = 0;

    if(productList && interval) {
        const discountType = frequency.dataset.discountType;
        const discountValue = frequency.dataset.discountValue;
        const otpDiscountValue = otpPriceDiscountEl.dataset.otpDiscountValue; // hardcoded 15% from data

        productList.querySelectorAll('.bundle-product-select-quantity-input').forEach(function(subProductInput, index){
            let quantity = parseInt(subProductInput.value);
            let price = parseInt(subProductInput.dataset.price);
            let discount = 0;
            let otpDiscount = 0;
            switch(discountType){
                case 'percentage':
                    discount = (price * parseInt(discountValue)) / 100;
                    otpDiscount = Math.floor((price * parseInt(otpDiscountValue).toFixed(0)) / 100);
                    break;
                case 'fixed_amount':
                    discount = parseInt(discountValue);
                    break;
                case 'price':
                    discount = price - parseInt(discountValue);
                    break;
            }
            subSubtotal = subSubtotal + (quantity * (price - discount.toFixed(0)));
            otpSubtotal = otpSubtotal + (quantity * price);
            otpSubtotalDiscounted = otpSubtotalDiscounted + (quantity * (price - otpDiscount.toFixed(0)));
        });
    }

    if (otpSubtotal > 0) {
      otpSubtotal = otpSubtotal + productBasePrice;
    }

    if (subSubtotal > 0) {
        const discountType = frequency.dataset.discountType;
        const discountValue = frequency.dataset.discountValue;
        let discount = 0;
        switch(discountType){
            case 'percentage':
                discount = (productBasePrice * parseInt(discountValue)) / 100;
                break;
            case 'fixed_amount':
                discount = parseInt(discountValue);
                break;
                case 'price':
                discount = productBasePrice - parseInt(discountValue);
                break;
            }
        if(discount < 0) { discount = 0; }
        subSubtotal = subSubtotal + (productBasePrice - discount.toFixed(0));
    }

    const formatPrice = (price) => {
      return window.formattedPrice(price/100)
    }

    otpPriceEls.forEach(function(el){
      (otpSubtotal > 0) ? (el.innerHTML = formatPrice(otpSubtotal)) : (el.innerHTML = '');
    });

    otpDiscountPriceEls.forEach(function(el){
      (otpSubtotalDiscounted > 0) ? (el.innerHTML = formatPrice(otpSubtotalDiscounted)) : (el.innerHTML = '');
    });

    subPriceEls.forEach(function(el){
      (subSubtotal > 0) ? (el.innerHTML = formatPrice(subSubtotal)) : (el.innerHTML = '');
    });

    curPriceEls.forEach(function(el){
        (otpSubtotalDiscounted > 0) ? (el.querySelector('.button-otp-text').innerHTML = formatPrice(otpSubtotalDiscounted)) : (el.innerHTML = '');
        (subSubtotal > 0) ? (el.querySelector('.button-sub-text').innerHTML = formatPrice(subSubtotal)) : (el.innerHTML = '');
    });

    emit('update-card-price', {
      productId: product.value.id,
      price: formatPrice(subSubtotal)
    });
  }

  const isVariantDrpdwnOpen = ref(false)

  const dropdownElement = ref(null)

  /*const handleClickOutside = (event) => {
    if (dropdownElement.value && !dropdownElement.value.contains(event.target)) {
      isVariantDrpdwnOpen.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
  })*/

  const toggleVariantDrpdwn = (event) => {
    event.stopPropagation()
    isVariantDrpdwnOpen.value = !isVariantDrpdwnOpen.value
  }

  defineExpose({isVariantDrpdwnOpen})
</script>
